import { Activity, Anchor, ArrowDown, ArrowUp, Layers, Target, TrendingUp, TrendingDown, Zap, ShieldCheck, BrainCircuit, Sparkles, Eye, EyeOff } from 'lucide-react'
import { useEffect, useMemo, useRef, memo, useState } from 'react'
import { useDashboardData } from '../hooks/useDashboardData'
// FREEZE FIX v4: useIntradayHistory REMOVED — it was a dead subscription.
// `history` was never used in the render output, but it caused this heavy component
// to re-render every time history data arrived (triggering calculateSupportResistance,
// useTradeSignals, useIndexIndicators, etc. to all re-run).
import { useSettingsStore } from '../stores/settingsStore'
import { useTradeSignals } from '../hooks/useTradeSignals'
import { calculateSupportResistance } from '../lib/analytics'
import { jotaiStore, indexLtpAtoms, marketMetaAtoms } from '../stores/liveTickAtoms'
import { useMarketStore } from '../services/store'
// Removed cn import as we are using template literals
import type { AnalysisResult, MarketData } from '../types'
import { GeometricAccordion } from './ui/GeometricAccordion'
import { MetricCard } from './ui/MaterialCard' // Import path correct for V4
import { NumberTicker } from './ui/NumberTicker'
import { Skeleton } from './ui/skeleton'
import { formatIndianScale } from '../lib/table-utils'
import { useIndexIndicators } from '../hooks/useIndexIndicators'
import { calculateTradeIntelligence } from '../lib/trade-intelligence'
import { useStrikeIndicators } from '../hooks/useStrikeIndicators'

// ─────────────────────────────────────────────────────────────────────────────
// IMPERATIVE DOM UPDATERS (Zero React Re-Renders)
// ─────────────────────────────────────────────────────────────────────────────

/**
 * A highly optimized Number Ticker that bypasses React completely.
 * It subscribes to Jotai imperatively and directly mutates the DOM's textContent.
 * Uses requestAnimationFrame to maintain the smooth NumberTicker sliding effect.
 */
const LiveDOMNumberTicker = ({ symbol, type, fallback, decimals = 2, prefix = '', suffix = '' }: { symbol: string, type: 'ltp' | 'vix' | 'dayChange' | 'vixChange', fallback: number, decimals?: number, prefix?: string, suffix?: string }) => {
    const spanRef = useRef<HTMLSpanElement>(null);
    const displayValueRef = useRef<number>(fallback);
    const animationRef = useRef<number | null>(null);

    useEffect(() => {
        const atom = type === 'ltp' ? indexLtpAtoms : marketMetaAtoms;
        
        const animateTo = (endValue: number) => {
            const startValue = displayValueRef.current;
            if (Math.abs(endValue - startValue) < Number.EPSILON) return;
            
            let startTime: number | null = null;
            const duration = 500;
            
            const tick = (timestamp: number) => {
                if (!startTime) startTime = timestamp;
                const progress = Math.min((timestamp - startTime) / duration, 1);
                const ease = 1 - Math.pow(1 - progress, 4); // ease out quart
                const current = startValue + (endValue - startValue) * ease;
                
                displayValueRef.current = current;
                if (spanRef.current) {
                    spanRef.current.textContent = `${prefix}${current.toFixed(decimals)}${suffix}`;
                }
                
                if (progress < 1) {
                    animationRef.current = requestAnimationFrame(tick);
                }
            };
            
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
            animationRef.current = requestAnimationFrame(tick);
        };

        const unsubscribe = jotaiStore.sub(atom as any, () => {
            const state = jotaiStore.get(atom as any);
            if (type === 'ltp') {
                const ltp = (state as Record<string, number>)[symbol];
                if (ltp !== undefined) animateTo(ltp);
            } else {
                const meta = (state as Record<string, any>)[symbol];
                if (meta !== undefined) {
                    let val: number | undefined;
                    if (type === 'vix') val = meta.vix;
                    else if (type === 'dayChange') val = meta.dayChange;
                    else if (type === 'vixChange') val = meta.vixChange;
                    if (val !== undefined) animateTo(val);
                }
            }
        });
        
        // Setup initial without animation
        const state = jotaiStore.get(atom as any);
        if (type === 'ltp') {
            const ltp = (state as Record<string, number>)[symbol];
            if (ltp !== undefined) {
                displayValueRef.current = ltp;
                if (spanRef.current) spanRef.current.textContent = `${prefix}${ltp.toFixed(decimals)}${suffix}`;
            }
        } else {
            const meta = (state as Record<string, any>)[symbol];
            if (meta !== undefined) {
                let val: number | undefined;
                if (type === 'vix') val = meta.vix;
                else if (type === 'dayChange') val = meta.dayChange;
                else if (type === 'vixChange') val = meta.vixChange;
                if (val !== undefined) {
                    displayValueRef.current = val;
                    if (spanRef.current) spanRef.current.textContent = `${prefix}${val.toFixed(decimals)}${suffix}`;
                }
            }
        }
        
        return () => {
            unsubscribe();
            if (animationRef.current) cancelAnimationFrame(animationRef.current);
        };
    }, [symbol, type, decimals, prefix, suffix]);
    
    return <span ref={spanRef}>{prefix}{fallback.toFixed(decimals)}{suffix}</span>
}

/**
 * Imperatively updates the trend SVG without triggering React renders.
 */
const LiveTrendArrow = ({ symbol, type, fallbackDir }: { symbol: string, type: 'ltp' | 'vix', fallbackDir: 'up' | 'down' | 'neutral' }) => {
    const containerRef = useRef<HTMLSpanElement>(null);
    const lastValRef = useRef<number | null>(null);

    useEffect(() => {
        const atom = type === 'ltp' ? indexLtpAtoms : marketMetaAtoms;

        const updateArrow = (newVal: number) => {
            if (lastValRef.current === null) {
                lastValRef.current = newVal;
                return;
            }
            if (Math.abs(newVal - lastValRef.current) < 0.0001) return;
            
            const dir = newVal > lastValRef.current ? 'up' : 'down';
            lastValRef.current = newVal;

            if (containerRef.current) {
                if (dir === 'up') {
                    containerRef.current.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-up text-emerald-500 animate-bounce-short inline ml-1"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>`;
                } else {
                    containerRef.current.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down text-rose-500 animate-bounce-short inline ml-1"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>`;
                }
            }
        };

        const unsubscribe = jotaiStore.sub(atom as any, () => {
            const state = jotaiStore.get(atom as any);
            if (type === 'ltp') {
                const ltp = (state as Record<string, number>)[symbol];
                if (ltp !== undefined) updateArrow(ltp);
            } else {
                const meta = (state as Record<string, any>)[symbol];
                if (meta !== undefined && meta.vix !== undefined) updateArrow(meta.vix);
            }
        });

        // Initialize
        const state = jotaiStore.get(atom as any);
        if (type === 'ltp') {
            const ltp = (state as Record<string, number>)[symbol];
            if (ltp !== undefined) lastValRef.current = ltp;
        } else {
            const meta = (state as Record<string, any>)[symbol];
            if (meta !== undefined && meta.vix !== undefined) lastValRef.current = meta.vix;
        }

        return unsubscribe;
    }, [symbol, type]);

    const initialHtml = fallbackDir === 'up' 
        ? `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-up text-emerald-500 animate-bounce-short inline ml-1"><path d="m5 12 7-7 7 7"/><path d="M12 19V5"/></svg>`
        : fallbackDir === 'down'
            ? `<svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-arrow-down text-rose-500 animate-bounce-short inline ml-1"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>`
            : '';

    return <span ref={containerRef} dangerouslySetInnerHTML={{ __html: initialHtml }} />
}

type MarketSummaryCardsProps = {}

const getValueColorClass = ( val: number ) =>
    val > 0
        ? 'text-emerald-500 dark:text-emerald-400'
        : val < 0
            ? 'text-rose-500 dark:text-rose-400'
            : 'text-gray-400 dark:text-slate-500'

export const MarketSummaryCards: React.FC<MarketSummaryCardsProps> = memo( () =>
{
    const { marketData, analysis, baseOptions, baseSpot } = useDashboardData()
    const settingsMode = useSettingsStore(s => s.mode)
    const settingsRange = useSettingsStore(s => s.range)
    const settingsSymbol = useSettingsStore(s => s.symbol)
    const settingsExpiry = useSettingsStore(s => s.expiry)

    // Base Analysis for visual reference (Base S1 / R1)
    const baseAnalysis = useMemo( () =>
    {
        if ( !baseOptions || baseOptions.length === 0 ) return null
        return calculateSupportResistance(
            baseOptions,
            baseSpot || marketData?.spotPrice || 0,
            settingsMode,
            settingsRange,
            settingsSymbol,
        )
    }, [ baseOptions, baseSpot, marketData?.spotPrice, settingsMode, settingsRange, settingsSymbol ] )
    type TrendDirection = 'neutral' | 'up' | 'down'

    const prevAnalysisRef = useRef<AnalysisResult | null>( null )
    const prevMarketDataRef = useRef<MarketData | null>( null )

    const trends = useMemo( () =>
    {
        const defaultTrends: Record<string, TrendDirection> = {
            shift: 'neutral',
            support: 'neutral',
            support2: 'neutral',
            resistance: 'neutral',
            ltp: 'neutral',
            pcr: 'neutral',
            vix: 'neutral',
        }

        const prevAnalysis = prevAnalysisRef.current
        const prevMarketData = prevMarketDataRef.current

        if ( !analysis || !marketData ) return defaultTrends

        const getTrend = ( current: number, previous: number | undefined ): TrendDirection =>
        {
            if ( previous === undefined ) return 'neutral'
            const diff = current - previous
            if ( Math.abs( diff ) < 0.0001 ) return 'neutral'
            return diff > 0 ? 'up' : 'down'
        }

        // --- Cross-Symbol Cache Protection ---
        // If the user changed symbols (e.g., NIFTY -> SENSEX), the previous spot price / data is entirely irrelevant
        // so we must reset the comparators rather than calculating the delta between asset classes.
        if ( prevMarketData && prevMarketData.symbol !== marketData.symbol )
        {
            return defaultTrends
        }

        // Calculate trends based on history if available, else derive from daily changes
        const newTrends = {
            shift: prevAnalysis ? getTrend( analysis.shift, prevAnalysis.shift ) : 'neutral',
            support: prevAnalysis ? getTrend( analysis.supportStrike, prevAnalysis.supportStrike ) : 'neutral',
            support2: prevAnalysis ? getTrend( analysis.supportStrike2, prevAnalysis.supportStrike2 ) : 'neutral',
            resistance: prevAnalysis ? getTrend( analysis.resistanceStrike, prevAnalysis.resistanceStrike ) : 'neutral',
            shiftOld: prevAnalysis ? getTrend( analysis.shiftOld || 0, prevAnalysis.shiftOld || 0 ) : 'neutral',

            // For LTP and VIX, we prefer comparing against previous fetch,
            // but fallback to dayChange if first load
            ltp: prevMarketData
                ? getTrend( marketData.spotPrice, prevMarketData.spotPrice )
                : ( effectiveDayChange || 0 ) > 0
                    ? 'up'
                    : ( effectiveDayChange || 0 ) < 0
                        ? 'down'
                        : 'neutral',

            pcr: prevAnalysis ? getTrend( analysis.pcr || 0, prevAnalysis.pcr || 0 ) : 'neutral',
            vix: prevMarketData
                ? getTrend( marketData.vix || 0, prevMarketData.vix || 0 )
                : ( marketData.vixChange || 0 ) > 0
                    ? 'up'
                    : ( marketData.vixChange || 0 ) < 0
                        ? 'down'
                        : 'neutral',
        }

        return newTrends
    }, [ analysis, marketData ] )

    // FREEZE FIX v4: Removed useIntradayHistory — was never used in JSX

    // Fetch Trade Signals to get the accurate RSI Diff calculated from user criteria (R1/S1)
    const { scannerRsiData } = useTradeSignals( {
        symbol: marketData?.symbol || settingsSymbol,
        mode: settingsMode,
        expiry: marketData?.options?.[ 0 ]?.expiryDate || settingsExpiry,
    } )

    const rsiDiff = scannerRsiData?.rsiDiff !== undefined ? scannerRsiData.rsiDiff : null

    // Fetch Index Lows
    const indexSymbol = marketData?.symbol || settingsSymbol
    const historicalDate = useSettingsStore(s => s.historicalDate);
    const { indexIndicators, loading: indexLoading } = useIndexIndicators( indexSymbol, historicalDate )

    let effectiveDayChange = marketData.dayChange || 0
    let effectiveDayChangeP = marketData.dayChangeP || 0

    // Update refs after render to store "previous" state for next update
    useEffect( () =>
    {
        if ( analysis ) prevAnalysisRef.current = analysis
        if ( marketData ) prevMarketDataRef.current = marketData
    }, [ analysis, marketData ] )


    const { ceIndicators, peIndicators } = useStrikeIndicators({
        selectedCeStrike: analysis?.v1Strike || marketData?.spotPrice || 0,
        selectedPeStrike: analysis?.v1Strike || marketData?.spotPrice || 0,
        activeMarketData: marketData,
        symbol: marketData?.symbol || '',
        historicalDate,
    });

    // FREEZE FIX: Subscribe to history LENGTH (primitive), NOT the array reference, to trigger recalculation when CSV loads
    const inspectionSource = useSettingsStore.getState().inspectionSource;
    const activeSymbol = marketData?.symbol || '';
    const historyLength = useMarketStore( ( state ) => {
        if ( inspectionSource === 'HistoricalSnapshot' ) return state.historicalHistory?.[ activeSymbol ]?.length ?? 0
        if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) return state.rangeHistory?.[ activeSymbol ]?.length ?? 0
        return state.intradayHistory?.[ activeSymbol ]?.length ?? 0
    } )

    // Calculate Trade Intelligence for the Summary Card
    const tradeIntel = useMemo(() => {
        if (!marketData || !analysis) return null;
        const store = useMarketStore.getState()
        let historyArray: any[] = []
        if ( inspectionSource === 'HistoricalSnapshot' ) {
            historyArray = store.historicalHistory?.[ activeSymbol ] || []
        } else if ( inspectionSource === 'RangeIntraday' || inspectionSource === 'RangeEOD' ) {
            historyArray = store.rangeHistory?.[ activeSymbol ] || []
        } else {
            historyArray = store.intradayHistory?.[ activeSymbol ] || []
        }

        return calculateTradeIntelligence({
            marketData,
            analysis,
            history: historyArray,
            mode: settingsMode,
            rsiData: scannerRsiData || undefined,
            ceIndicators: ceIndicators, 
            peIndicators: peIndicators,
            ceStrikeLtp: undefined,
            peStrikeLtp: undefined,
        });
    }, [marketData, analysis, settingsMode, scannerRsiData, ceIndicators, peIndicators, historyLength, inspectionSource, activeSymbol]);

    // Internal state moved to GeometricAccordion via storageKey

    if ( !marketData || !analysis )
        return (
            <div className="flex flex-col gap-4 sm:gap-5 mb-2 sm:mb-4 relative min-h-[140px]">
                <div className="flex justify-end w-full mb-[-14px] sm:mb-[-18px] relative z-20 pr-1">
                    <Skeleton className="h-6 w-32 rounded-md opacity-20" />
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5 mt-2">
                    { Array( 6 )
                        .fill( 0 )
                        .map( ( _, i ) => (
                            <Skeleton key={ i } className="h-28 w-full rounded-2xl opacity-20" />
                        ) ) }
                </div>
            </div>
        )

    // V2 Logic for variables
    const ceCount = analysis.variance?.ceCount || 0
    const peCount = analysis.variance?.peCount || 0
    const totalCount = analysis.variance?.totalCount || 0

    const supportS = analysis.supportStrike || 0
    const supportS2 = analysis.supportStrike2 || 0
    const resistanceR = analysis.resistanceStrike || 0
    const shiftVal = analysis.shift || 0
    const shiftOldVal = analysis.shiftOld || 0
    const shiftVVal = analysis.shiftV || 0
    // Variance Levels -> Now mapped to Top 3 Combined Volume Strikes
    const v1Strike = analysis.v1Strike || 0
    const v2Strike = analysis.v2Strike || 0
    const v3Strike = analysis.v3Strike || 0
    const v1Vol = analysis.v1Vol || 0
    const v2Vol = analysis.v2Vol || 0
    const v3Vol = analysis.v3Vol || 0

    const indexLtp = marketData.spotPrice || 0

    const levelsList = [
        { label: '5M', low: indexIndicators?.first5Min?.low, high: indexIndicators?.first5Min?.high },
        { label: '15M', low: indexIndicators?.first15Min?.low, high: indexIndicators?.first15Min?.high },
        { label: '1D', low: indexIndicators?.prevDay?.low, high: indexIndicators?.prevDay?.high },
        { label: '1W', low: indexIndicators?.prevWeek?.low, high: indexIndicators?.prevWeek?.high },
        { label: '1M', low: indexIndicators?.prevMonth?.low, high: indexIndicators?.prevMonth?.high },
        { label: '2M', low: indexIndicators?.prev2Months?.low, high: indexIndicators?.prev2Months?.high },
    ]
    const brokenLowsCount = levelsList.filter( ( l ) => l.low && indexLtp < l.low ).length
    const crossedHighsCount = levelsList.filter( ( l ) => l.high && indexLtp > l.high ).length
    const totalLevels = levelsList.length

    return (
        <div className="flex flex-col gap-4 sm:gap-5 mb-2 sm:mb-4 animate-in fade-in duration-700 ease-out relative min-h-[140px]">
            <GeometricAccordion
                title={`Market Summary (${marketData.symbol})`}
                badge={<BrainCircuit size={18} />}
                storageKey={`market_summary_expanded_${marketData.symbol}`}
            >
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-6 gap-3 sm:gap-4 lg:gap-5 mt-2">
                <MetricCard
                    title={ `${marketData.symbol} LTP` }
                    value={ <LiveDOMNumberTicker symbol={marketData.symbol} type="ltp" fallback={ marketData.spotPrice || 0 } decimals={ 2 } /> }
                    change={ effectiveDayChange }
                    changeP={ effectiveDayChangeP }
                    icon={ <Activity className="text-blue-500 dark:text-blue-400" /> }
                    subtext={
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">Trend:</span>{ ' ' }
                            <LiveTrendArrow symbol={marketData.symbol} type="ltp" fallbackDir={ trends.ltp as any } />
                        </div>
                    }
                    compact={ true }
                    className="bg-white dark:bg-slate-800/95 rounded-[20px] hover:shadow-[0_10px_40px_-10px_rgba(59,130,246,0.15)] transition-all duration-300"
                />
                <MetricCard
                    title="Bias Counters"
                    value={
                        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0 relative z-10">
                            <span className={ `flex items-center font-bold text-lg ${getValueColorClass( ceCount )}` }>
                                <span className="text-xs font-medium text-gray-400 dark:text-slate-500 mr-1">C:</span>
                                <NumberTicker value={ ceCount } prefix={ ceCount > 0 ? '+' : '' } />
                            </span>

                            <span className={ `flex items-center font-bold text-lg ${getValueColorClass( peCount )}` }>
                                <span className="text-xs font-medium text-gray-400 dark:text-slate-500 mr-1">P:</span>
                                <NumberTicker value={ peCount } prefix={ peCount > 0 ? '+' : '' } />
                            </span>
                        </div>
                    }
                    subtext={
                        <div className="flex items-center justify-between w-full pt-1 border-t border-slate-100 dark:border-slate-800 px-0.5">
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 tracking-wider">
                                    CNT
                                </span>
                                <span
                                    className={ `text-xs font-black px-1 py-0.5 rounded ${getValueColorClass( totalCount )} bg-slate-50 dark:bg-slate-800/50` }
                                >
                                    <NumberTicker value={ totalCount } prefix={ totalCount > 0 ? '+' : '' } />
                                </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                                <span className="text-[9px] font-bold text-slate-400 dark:text-slate-500 tracking-wider">
                                    VAR
                                </span>
                                <span
                                    className={ `text-xs font-black px-1 py-0.5 rounded ${getValueColorClass( analysis.variance?.finalVariance || 0 )} bg-slate-50 dark:bg-slate-800/50` }
                                >
                                    <NumberTicker value={ analysis.variance?.finalVariance || 0 } decimals={ 2 } suffix="%" />
                                </span>
                            </div>
                        </div>
                    }
                    icon={ <Layers className="text-violet-500 dark:text-violet-400" /> }
                    compact={ true }
                    className="bg-white dark:bg-slate-800/95 rounded-[20px] hover:shadow-[0_10px_40px_-10px_rgba(139,92,246,0.15)] transition-all duration-300 group"
                />

                {/* Split PCR / RSI Card */ }
                <MetricCard
                    title={
                        <div className="flex w-full justify-between items-center pr-2">
                            <span>PCR / RSI</span>
                        </div>
                    }
                    value={
                        <div className="flex w-full items-center justify-between gap-2">
                            {/* Left Half: PCR */ }
                            <div className="flex items-center justify-between gap-1 w-1/2 border-r border-slate-100 dark:border-slate-800 pr-2">
                                <div className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">
                                    PCR
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <NumberTicker value={ analysis.pcr || 0 } decimals={ 2 } />
                                    <TrendArrow dir={ trends.pcr as any } />
                                </div>
                            </div>

                            {/* Right Half: RSI Difference */ }
                            <div className="flex items-center justify-between gap-1 w-1/2 pl-2">
                                <div className="text-[10px] text-slate-400 dark:text-slate-500 font-bold uppercase tracking-wider">
                                    RSI Δ
                                </div>
                                <div className="flex items-center">
                                    { rsiDiff !== null ? (
                                        <span className={ `font-bold text-lg ${getValueColorClass( rsiDiff )}` }>
                                            <NumberTicker value={ rsiDiff } decimals={ 1 } prefix={ rsiDiff > 0 ? '+' : '' } />
                                        </span>
                                    ) : (
                                        <span className="text-base text-gray-300 dark:text-slate-600">-</span>
                                    ) }
                                </div>
                            </div>
                        </div>
                    }
                    subtext={
                        <div className="flex items-center justify-between w-full mt-2 pt-1 border-t border-slate-50 dark:border-slate-800">
                            {/* PCR Status */ }
                            <span
                                className={ `px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${( analysis.pcr || 0 ) > 1 ? 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400' : 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400'}` }
                            >
                                { ( analysis.pcr || 0 ) > 1 ? 'Bullish' : 'Bearish' }
                            </span>

                            {/* RSI Status */ }
                            { rsiDiff !== null && (
                                <span
                                    className={ `px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide ${rsiDiff > 0 ? 'bg-emerald-50 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-rose-50 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400'}` }
                                >
                                    { rsiDiff > 0 ? 'Bullish' : 'Bearish' }
                                </span>
                            ) }
                        </div>
                    }
                    icon={
                        <TrendingUp
                            className={
                                ( analysis.pcr || 0 ) > 1
                                    ? 'text-rose-500 dark:text-rose-400'
                                    : 'text-emerald-500 dark:text-emerald-400'
                            }
                        />
                    }
                    compact={ true }
                    className="bg-white dark:bg-slate-800/95 rounded-[20px] hover:shadow-[0_10px_40px_-10px_rgba(99,102,241,0.15)] transition-all duration-300"
                />
                {/* Resistance Levels - Moved BEFORE Support Levels as requested */ }
                <MetricCard
                    title="Resistance Levels"
                    value={
                        <div className="flex items-center gap-1 text-rose-600 dark:text-rose-400 font-mono">
                            { resistanceR > 0 ? <NumberTicker value={ resistanceR } /> : '-' }{ ' ' }
                            <TrendArrow dir={ trends.resistance } />
                        </div>
                    }
                    subtext={
                        <div className="flex flex-col gap-1.5 mt-1 border-t border-slate-50 dark:border-slate-800 pt-1.5 min-h-[36px]">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-400 dark:text-slate-500 text-[10px] font-bold uppercase">
                                        Base R1
                                    </span>
                                    <span className="text-emerald-600 dark:text-emerald-400 font-bold bg-emerald-50 dark:bg-emerald-900/30 px-1.5 rounded-md text-[11px] font-mono border border-emerald-100/50 dark:border-emerald-800/50">
                                        { baseAnalysis?.resistance || '-' }
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-400 dark:text-slate-500 text-[10px] font-bold uppercase">
                                        Shift
                                    </span>
                                    <span className="text-indigo-700 dark:text-indigo-300 font-bold bg-indigo-50 dark:bg-indigo-900/30 px-1.5 rounded-md text-[11px] font-mono border border-indigo-100/50 dark:border-indigo-800/50">
                                        { shiftVal > 0 ? <NumberTicker value={ shiftVal } /> : '-' }
                                    </span>
                                </div>
                            </div>
                        </div>
                    }
                    icon={ <ShieldCheck className="text-rose-500 dark:text-rose-400" /> }
                    compact={ true }
                    className="bg-white dark:bg-slate-800/95 rounded-[20px] hover:shadow-[0_10px_40px_-10px_rgba(244,63,94,0.15)] transition-all duration-300"
                />

                {/* Support Levels - Moved AFTER Resistance Levels as requested */ }
                <MetricCard
                    title="Support Levels"
                    value={
                        <div className="flex items-center gap-1 text-emerald-600 dark:text-emerald-400">
                            { supportS > 0 ? <NumberTicker value={ supportS } /> : '-' } <TrendArrow dir={ trends.support } />
                        </div>
                    }
                    subtext={
                        <div className="flex flex-col gap-1.5 mt-1 border-t border-slate-50 dark:border-slate-800 pt-1.5 min-h-[36px]">
                            <div className="flex items-center justify-between w-full">
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-400 dark:text-slate-500 text-[10px] font-bold uppercase">
                                        Base S1
                                    </span>
                                    <span className="text-rose-600 dark:text-rose-400 font-bold bg-rose-50 dark:bg-rose-900/30 px-1.5 rounded-md text-[11px] font-mono border border-rose-100/50 dark:border-rose-800/50">
                                        { baseAnalysis?.support || '-' }
                                    </span>
                                </div>
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-400 dark:text-slate-500 text-[10px] font-bold uppercase">
                                        S1-Sub
                                    </span>
                                    <span className="text-emerald-700 dark:text-emerald-300 font-bold bg-emerald-50 dark:bg-emerald-900/30 px-1.5 rounded-md text-[11px] font-mono border border-emerald-100/50 dark:border-emerald-800/50">
                                        { supportS2 > 0 ? <NumberTicker value={ supportS2 } /> : '-' }
                                    </span>
                                </div>
                            </div>
                        </div>
                    }
                    icon={ <Anchor className="text-emerald-500 dark:text-emerald-400" /> }
                    compact={ true }
                    className="bg-white dark:bg-slate-800/95 rounded-[20px] hover:shadow-[0_10px_40px_-10px_rgba(16,185,129,0.15)] transition-all duration-300"
                />
                <MetricCard
                    title="India VIX"
                    value={ <LiveDOMNumberTicker symbol={marketData.symbol} type="vix" fallback={ marketData.vix || 0 } decimals={ 2 } /> }
                    change={ marketData.vixChange || 0 }
                    changeP={ marketData.vixChangeP || 0 }
                    subtext={
                        <div className="flex items-center gap-1">
                            <span className="text-xs text-gray-500 dark:text-slate-400 font-medium">Trend:</span>{ ' ' }
                            <LiveTrendArrow symbol={marketData.symbol} type="vix" fallbackDir={ trends.vix as any } />
                        </div>
                    }
                    icon={ <Zap className="text-amber-500 dark:text-amber-400" /> }
                    compact={ true }
                    className="bg-white dark:bg-slate-800/95 rounded-[20px] hover:shadow-[0_10px_40px_-10px_rgba(245,158,11,0.15)] transition-all duration-300"
                />
            </div>
            </GeometricAccordion>

            {/* Index Lows Row */ }
            <GeometricAccordion
                title={`${marketData.symbol} LEVELS`}
                badge={<Target size={18} />}
                storageKey={`market_levels_expanded_${marketData.symbol}`}
                headerRight={
                    <div className="flex items-center gap-1.5 opacity-90">
                        <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold bg-rose-50 dark:bg-rose-500/10 text-rose-700 dark:text-rose-300 px-2 py-0.5 rounded border border-rose-200/60 dark:border-rose-500/30 shadow-sm pt-1">
                            <span className="pt-px">LOWS BROKEN</span>
                            <span className="text-xs sm:text-[13px] font-black leading-none">
                                { brokenLowsCount }/{ totalLevels }
                            </span>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] sm:text-[11px] font-bold bg-emerald-50 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 px-2 py-0.5 rounded border border-emerald-200/60 dark:border-emerald-500/30 shadow-sm pt-1">
                            <span className="pt-px">HIGHS CROSSED</span>
                            <span className="text-xs sm:text-[13px] font-black leading-none">
                                { crossedHighsCount }/{ totalLevels }
                            </span>
                        </div>
                    </div>
                }
            >
            <div className="bg-white dark:bg-slate-800/95 rounded-[24px] shadow-[0_8px_30px_-6px_rgba(0,0,0,0.04)] p-4 sm:p-5 transition-all duration-300 border-0">
                <div className="flex items-center justify-end mb-3 px-2 border-b border-slate-100 dark:border-slate-800 pb-2">

                    {/* Today OHL */ }
                    <div className="hidden sm:flex items-center gap-4 ml-auto mr-4 bg-slate-50/50 dark:bg-slate-800/30 px-3 py-1 rounded-md border border-slate-100 dark:border-slate-800/80">
                        <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 tracking-widest pt-[2px]">OPEN</span>
                            <span className="text-sm font-mono font-bold text-slate-700 dark:text-slate-300">
                                { indexIndicators?.todayOpen ? indexIndicators.todayOpen.toFixed( 1 ) : '-' }
                            </span>
                        </div>
                        <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 tracking-widest pt-[2px]">HIGH</span>
                            <span className="text-sm font-mono font-bold text-emerald-600 dark:text-emerald-400">
                                { indexIndicators?.todayHigh ? indexIndicators.todayHigh.toFixed( 1 ) : '-' }
                            </span>
                        </div>
                        <div className="w-px h-3 bg-slate-200 dark:bg-slate-700" />
                        <div className="flex items-center gap-1.5">
                            <span className="text-[11px] font-black text-slate-400 dark:text-slate-500 tracking-widest pt-[2px]">LOW</span>
                            <span className="text-sm font-mono font-bold text-rose-600 dark:text-rose-400">
                                { indexIndicators?.todayLow ? indexIndicators.todayLow.toFixed( 1 ) : '-' }
                            </span>
                        </div>
                    </div>

                    <div className="text-[10px] sm:text-xs font-bold text-slate-500 dark:text-slate-400">
                        IDX LTP: <span className="text-slate-800 dark:text-slate-100 font-extrabold ml-1">{ indexLtp.toFixed( 0 ) }</span>
                    </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-6 gap-2 sm:gap-2">
                    { levelsList.map( ( item ) =>
                    {
                        const isBroken = item.low ? indexLtp < item.low : false
                        const isCrossed = item.high ? indexLtp > item.high : false

                        const isLtpAboveHigh = item.high !== undefined ? indexLtp > item.high : false
                        const isLtpBelowHigh = item.high !== undefined ? indexLtp < item.high : false

                        const isLtpAboveLow = item.low !== undefined ? indexLtp > item.low : false
                        const isLtpBelowLow = item.low !== undefined ? indexLtp < item.low : false

                        return (
                            <div key={ item.label } className="relative overflow-hidden flex flex-col py-2 px-2.5 sm:px-3 rounded-[14px] bg-slate-50/80 dark:bg-slate-900/50 border-0 shadow-none hover:bg-slate-100 dark:hover:bg-slate-900 transition-all duration-300 group">
                                {/* Accent Line */ }
                                <div className={ `absolute top-0 inset-x-0 h-[3px] transition-colors duration-300 ${isCrossed ? 'bg-emerald-400' : isBroken ? 'bg-rose-400' : 'bg-indigo-300'}` }></div>

                                {/* Header Row */ }
                                <div className="flex justify-between items-center w-full mb-1.5">
                                    <span className="text-[10px] sm:text-[11px] font-black text-emerald-600 dark:text-emerald-500 opacity-90">HI</span>
                                    <span className="text-[11px] sm:text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-widest">{ item.label }</span>
                                    <span className="text-[10px] sm:text-[11px] font-black text-rose-600 dark:text-rose-500 opacity-90">LO</span>
                                </div>

                                {/* Content Row */ }
                                <div className="flex justify-between items-center w-full mt-auto pt-0.5">
                                    {/* High Side */ }
                                    <div className={ `flex items-center justify-start w-[45%] font-mono text-[10px] sm:text-xs font-extrabold tracking-tight ${isLtpAboveHigh ? 'text-emerald-600 dark:text-emerald-400' : isLtpBelowHigh ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-300'}` }>
                                        <span>{ item.high ? Math.round( item.high ) : '-' }</span>
                                        { isLtpAboveHigh && <ArrowUp size={ 12 } strokeWidth={ 3 } className="ml-0.5 shrink-0" /> }
                                        { isLtpBelowHigh && <ArrowDown size={ 12 } strokeWidth={ 3 } className="ml-0.5 shrink-0" /> }
                                    </div>

                                    {/* Divider */ }
                                    <div className="w-px h-3 bg-slate-200 dark:bg-slate-700 mx-0.5 shrink-0"></div>

                                    {/* Low Side */ }
                                    <div className={ `flex items-center justify-end w-[45%] font-mono text-[10px] sm:text-xs font-extrabold tracking-tight ${isLtpAboveLow ? 'text-emerald-600 dark:text-emerald-400' : isLtpBelowLow ? 'text-rose-600 dark:text-rose-400' : 'text-slate-600 dark:text-slate-300'}` }>
                                        { isLtpAboveLow && <ArrowUp size={ 12 } strokeWidth={ 3 } className="mr-0.5 shrink-0" /> }
                                        { isLtpBelowLow && <ArrowDown size={ 12 } strokeWidth={ 3 } className="mr-0.5 shrink-0" /> }
                                        <span>{ item.low ? Math.round( item.low ) : '-' }</span>
                                    </div>
                                </div>
                            </div>
                        )
                    } ) }
                </div>
            </div>
            </GeometricAccordion>
        </div>
    )
} )

// Helper to render trend arrow (Prevent Re-creation)
const TrendArrow = memo( ( { dir }: { dir: 'up' | 'down' | 'neutral' } ) =>
{
    if ( dir === 'up' )
        return <ArrowUp size={ 12 } className="text-emerald-500 animate-bounce-short inline ml-1" strokeWidth={ 3 } />
    if ( dir === 'down' )
        return <ArrowDown size={ 12 } className="text-rose-500 animate-bounce-short inline ml-1" strokeWidth={ 3 } />
    return null
} )
